import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { adminConfirmationPath, formatResponseDate, getBpp, getProDecision, ProDecision } from "../helper";
import { useUser } from "../../../hooks/useUser";
import { isInternal, isExpertTeam, canDownloadConfirmation, SOW_ACTIVE_STATUSES } from "../../../utils";
import { downloadConfirmationDetails } from "../../../utils/confirmationDetailsExport";
import axiosInstant from "../../../api";
import { getSowCountersignUrl, downloadProjectExpertSow, getUserSows } from "../../../api/endpoints";
import ExpertSowSignModal from "../../../components/ExpertSowSignModal";
import { openPdfFromApi } from "../../../utils/openPdfFromApi";

// Status pill palettes, shared by STATUS_CONFIG and SOW_PILL_CONFIG below so a
// given state looks identical wherever it appears.
//
// LIGHT keeps the exact Material pastels these shipped with. DARK cannot reuse
// them: a pastel FILL on the dark card is a bright blob, and the mid-tone ink on
// it drops to ~2:1. Each family instead becomes a translucent wash of its OWN
// hue plus a light ink, which is how --up-bg/--down-bg are built.
//
// The amber wash is a literal, not a token pair, for two reasons: there is no
// amber token, and Tailwind SILENTLY DROPS opacity modifiers on var()-backed
// colours (dark:bg-up/15 would emit nothing), so token families can only be used
// where a ready-made *-bg token already exists.
const PILL_GREEN =
    "bg-[#E8F5E9] text-[#2E7D32] border-[#C8E6C9] dark:bg-up-bg dark:text-up dark:border-border-token";
const PILL_RED =
    "bg-[#FBE9E7] text-[#C62828] border-[#FFCCBC] dark:bg-down-bg dark:text-down dark:border-border-token";
const PILL_AMBER =
    "bg-[#FFF8E1] text-[#E65100] border-[#FFE0B2] dark:bg-[#7A5B12]/30 dark:text-[#E0BE73] dark:border-border-token";
const PILL_GOLD =
    "bg-[#F2EBE2] text-[#A07B2E] border-[#E4CBA8] dark:bg-tint dark:text-accent-text dark:border-border-token";
// Same amber family as PILL_AMBER in dark; light keeps the warm notice pair the
// pitch-off pill shipped with.
const PILL_PITCH_OFF =
    "bg-[#FDF2E3] text-[#9A5B00] border-[#F5D9A8] dark:bg-[#7A5B12]/30 dark:text-[#E0BE73] dark:border-border-token";

const STATUS_CONFIG: Record<Exclude<ProDecision, null>, { label: string; cls: string }> = {
    opted_in: { label: "Opted In", cls: PILL_GREEN },
    opted_out: { label: "Opted Out", cls: PILL_RED },
    notified: { label: "Pending", cls: PILL_AMBER },
    pending: { label: "Pending", cls: PILL_AMBER }
};

// SOW pill states, config-driven like STATUS_CONFIG above. `action` marks
// the interactive ones (rendered as buttons).
type SowPillKey =
    | "awaiting"
    | "declined"
    | "voided"
    | "countersign"
    | "countersignDeclined"
    | "countersignVoided"
    | "executed"
    | "onFile";
const SOW_PILL_CONFIG: Record<
    SowPillKey,
    {
        label: string;
        title: string;
        cls: string;
        action?: "countersign" | "download";
    }
> = {
    awaiting: {
        label: "SOW: Awaiting Expert",
        title: "The expert has not signed their Statement of Work yet",
        cls: PILL_AMBER
    },
    declined: {
        label: "SOW Declined",
        title: "The expert declined their Statement of Work — follow up with them; re-issue by un-confirming and re-confirming them (or editing their rate)",
        cls: PILL_RED
    },
    voided: {
        label: "SOW Voided",
        title: "This Statement of Work was voided — re-issue by un-confirming and re-confirming the expert (or editing their rate)",
        cls: PILL_RED
    },
    countersign: {
        label: "Sign SOW",
        title: "The expert signed — countersign to move the selection to Pre-Production once every confirmed expert's SOW is executed",
        cls: PILL_GOLD,
        action: "countersign"
    },
    countersignDeclined: {
        label: "Countersign Declined",
        title: "The countersignature was declined — re-issue by un-confirming and re-confirming the expert (or editing their rate)",
        cls: PILL_RED
    },
    countersignVoided: {
        label: "Countersign Voided",
        title: "The countersign envelope was voided — re-issue by un-confirming and re-confirming the expert (or editing their rate)",
        cls: PILL_RED
    },
    executed: {
        label: "SOW Signed",
        title: "Fully executed — open the signed Statement of Work",
        cls: PILL_GREEN,
        action: "download"
    },
    // Recorded as already signed at internal confirm (no envelope was sent,
    // so there is nothing to open or countersign).
    onFile: {
        label: "SOW on file",
        title: "Recorded as already signed at internal confirm — no envelope to open",
        cls: PILL_GREEN
    }
};

const PITCH_OFF_PILL = {
    label: "Pitch notifications off",
    title: "This expert turned off pitch notifications, so pitches and reminders will not reach them",
    cls: PILL_PITCH_OFF
};

const PILL_CLASS =
    "inline-flex items-center gap-[5px] px-[10px] py-[2px] rounded-[20px] text-[11px] font-semibold whitespace-nowrap";

interface Props {
    expert: any;
    project?: any;
    className?: string;
    forceHide?: boolean;
    /**
     * Fired after the company countersign completes (and the SOW cycle has
     * been reconciled server-side). Parents pass their project refresh: the
     * countersign is what moves a selection to Pre-Production (TDC-1604), so
     * the status chip and the roster must re-pull or the page keeps showing
     * Scheduling until a reload.
     */
    onSigned?: () => void;
}

export const ExpertOptStatusBar: React.FC<Props> = ({ expert, project, className = "", forceHide = false, onSigned }) => {
    const navigate = useNavigate();
    const { role } = useUser();
    const [countersignOpen, setCountersignOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // Flips the pill immediately after signing; the next refetch reconciles
    // (the countersign endpoints live-repair stale rows server-side). Reset
    // whenever this instance is reused for a DIFFERENT expert — a stale true
    // must never claim another expert's SOW is executed.
    const [countersignedLocally, setCountersignedLocally] = useState(false);
    React.useEffect(() => {
        setCountersignedLocally(false);
    }, [expert?.userid]);
    if (forceHide || !isInternal(role)) return null;
    const bpp = getBpp(expert);
    const status = getProDecision(bpp);

    // Company countersign controls (two-envelope SOW flow), countersign roles only,
    // and only for internally-confirmed experts — the confirmed experts' cards
    // on the Expert Selection tab are where the SOWs live.
    const brandId = project?.brand_id || project?.brands?.find((b: any) => !b.rmn)?.id;
    const expertSowStatus = bpp?.expert_signing_status;
    const internalSowStatus = countersignedLocally ? "completed" : bpp?.internal_signing_status;
    const showSow =
        isExpertTeam(role) &&
        !!bpp?.internal_confirmed &&
        !!brandId &&
        !!project?.id &&
        !!expert?.userid &&
        [...SOW_ACTIVE_STATUSES, "declined", "voided"].includes(expertSowStatus || "");

    let sowPillKey: SowPillKey | null = null;
    if (showSow) {
        if (expertSowStatus === "declined") sowPillKey = "declined";
        else if (expertSowStatus === "voided") sowPillKey = "voided";
        else if (expertSowStatus === "sent") sowPillKey = "awaiting";
        else if (bpp?.sow_on_file) sowPillKey = "onFile";
        else if (internalSowStatus === "completed") sowPillKey = "executed";
        else if (internalSowStatus === "declined") sowPillKey = "countersignDeclined";
        else if (internalSowStatus === "voided") sowPillKey = "countersignVoided";
        else sowPillKey = "countersign";
    }

    // DocuSign reported signing_complete for the countersign session. The
    // webhook normally records it, but it can lag (or never reach a local
    // API), so first hit the expert's SOW listing — it live-repairs a stale
    // company status and runs the Scheduling → Pre-Production gate — then
    // let the parent re-pull the project so the status chip moves.
    const handleCountersigned = async () => {
        setCountersignedLocally(true);
        try {
            await axiosInstant(getUserSows(expert.userid));
        } catch {
            // Best-effort: the parent refetch below still reflects whatever
            // the server already knows; the webhook/janitor self-heal.
        }
        toast.success(
            "Countersigned. Once every confirmed expert's SOW is signed and countersigned, the selection moves to Pre-Production."
        );
        if (onSigned) onSigned();
    };

    if (!status && !sowPillKey) return null;

    const config = status ? STATUS_CONFIG[status] : null;
    const responseDate = status ? formatResponseDate(bpp?.created_at || bpp?.createdAt) : null;

    const handleViewDetails = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (project?.id && expert?.userid) {
            navigate(adminConfirmationPath(project.id, expert.userid));
        }
    };

    const handleDownloadDetails = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!project?.id || !expert?.userid || downloading) return;
        setDownloading(true);
        await downloadConfirmationDetails(project.id, expert.userid);
        setDownloading(false);
    };

    const sowActions: Record<string, (e: React.MouseEvent) => void> = {
        countersign: (e) => {
            e.stopPropagation();
            setCountersignOpen(true);
        },
        download: (e) => {
            e.stopPropagation();
            // openPdfFromApi rethrows so callers own the feedback (matches
            // ExpertProjectCard's handleViewSignedSow).
            openPdfFromApi(downloadProjectExpertSow(brandId, project.id, expert.userid)).catch(() =>
                toast.error("Couldn't open the Statement of Work. Try again.")
            );
        }
    };

    const sowPill = sowPillKey ? SOW_PILL_CONFIG[sowPillKey] : null;
    const sowPillCls = sowPill ? `${PILL_CLASS} border ${sowPill.cls}` : PILL_CLASS;

    return (
        <div
            // This bar sits INSIDE the white card, so light needs its original
            // near-white (#FAFAF8): a whisper against the card body, but clearly
            // still card rather than canvas. --surface-2 is a canvas-level fill —
            // once it cooled to #EAECF0 the bar read as background and the card
            // looked like it began below it. Dark keeps the token, where
            // --surface-2 IS the correct step up from the card.
            className={`flex items-center flex-wrap gap-3 px-4 py-2 bg-[#FAFAF8] dark:bg-surface-2 border-b border-border-token text-[12px] ${className}`}
        >
            {config && (
                <div className={`${PILL_CLASS} border ${config.cls}`}>
                    {/* bg-current so the dot always tracks the pill's own ink */}
                    <div className="w-[6px] h-[6px] rounded-full bg-current" />
                    {config.label}
                </div>
            )}
            {expert?.pitch_notifications_disabled && (
                <div
                    className={`${PILL_CLASS} border ${PITCH_OFF_PILL.cls}`}
                    title={PITCH_OFF_PILL.title}
                >
                    <div className="w-[6px] h-[6px] rounded-full bg-current" />
                    {PITCH_OFF_PILL.label}
                </div>
            )}
            {sowPill &&
                (sowPill.action ? (
                    <button
                        type="button"
                        onClick={sowActions[sowPill.action]}
                        className={`${sowPillCls} cursor-pointer hover:opacity-80 transition-opacity`}
                        title={sowPill.title}
                    >
                        {sowPill.label}
                    </button>
                ) : (
                    <div className={sowPillCls} title={sowPill.title}>
                        {sowPill.label}
                    </div>
                ))}
            {responseDate && (
                <div className="flex items-center gap-1 text-[11.5px] text-text-2 whitespace-nowrap">
                    <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {responseDate}
                </div>
            )}
            {project?.id && expert?.userid && canDownloadConfirmation(role) && (
                <button
                    type="button"
                    onClick={handleDownloadDetails}
                    disabled={downloading}
                    className="ml-auto w-[26px] h-[26px] rounded-full border border-border-token bg-surface flex items-center justify-center cursor-pointer text-muted hover:border-accent hover:text-accent-text hover:bg-tint transition-all shrink-0 disabled:cursor-wait disabled:opacity-60"
                    title="Download confirmation details (PDF, video and photos)"
                    aria-label="Download confirmation details"
                >
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={downloading ? "animate-pulse" : undefined}
                    >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                </button>
            )}
            {project?.id && expert?.userid && (
                <button
                    type="button"
                    onClick={handleViewDetails}
                    className={`${canDownloadConfirmation(role) ? "" : "ml-auto "}w-[26px] h-[26px] rounded-full border border-border-token bg-surface flex items-center justify-center cursor-pointer text-muted hover:border-accent hover:text-accent-text hover:bg-tint transition-all shrink-0`}
                    title="View confirmation details"
                    aria-label="View confirmation details"
                >
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                </button>
            )}
            {countersignOpen && (
                <ExpertSowSignModal
                    open={countersignOpen}
                    signingUrlPath={getSowCountersignUrl(brandId, project.id, expert.userid)}
                    title="Countersign Statement of Work"
                    onClose={() => setCountersignOpen(false)}
                    onSigned={handleCountersigned}
                />
            )}
        </div>
    );
};

export default ExpertOptStatusBar;
